// Modal on <dialog>: the browser keeps focus inside, makes the page behind inert and returns focus on close.
// openModal loads the URL into .pf-modal__content, shows the dialog and fires 'modal:loaded' with the entry point
// (data-cta) and the URL in event.detail; 'modal:hidden' fires once it's closed. Both bubble from the dialog
import {lockScroll} from '../utils/scroll-lock';
import {request} from '../utils/http';
import {on} from '../utils/events';

// Fallback if transitionend doesn't come (tab in the background, transition interrupted): a bit over $transition-slow
const CLOSE_FALLBACK = 400;

export async function openModal(target, href, cta) {
    const dialog = document.querySelector(target);

    // A unique query skips the 301 to the list page that browsers cached for the create URLs before it became a 302;
    // a cached 301 has no expiry, so this stays
    const url = new URL(href, window.location.href);
    url.searchParams.set('_', Date.now());
    const html = await (await request(url)).text();

    dialog.querySelector('.pf-modal__content').innerHTML = html;
    show(dialog);
    dialog.dispatchEvent(new CustomEvent('modal:loaded', {bubbles: true, detail: {cta, href}}));
}

// Slides out, then closes; the 'close' handler cleans up, it also runs when the browser closes the dialog itself
export function closeModal(dialog) {
    if (!dialog?.open || dialog.classList.contains('is-closing')) return;

    dialog.classList.add('is-closing');
    dialog.classList.remove('is-in');

    const box = dialog.querySelector('.pf-modal__dialog');
    const onEnd = (e) => e.target === box && e.propertyName === 'transform' && dialog.close();
    const timer = setTimeout(() => dialog.close(), CLOSE_FALLBACK);
    box.addEventListener('transitionend', onEnd);
    // Run by the 'close' handler, so a modal reopened right away isn't closed by this slide-out
    dialog.cancelClosing = () => {
        box.removeEventListener('transitionend', onEnd);
        clearTimeout(timer);
    };
}

function show(dialog) {
    if (dialog.open) return;

    lockScroll(true);
    dialog.showModal();
    // The browser focuses the first control: a ring on the close button, the keyboard on mobile for a field
    dialog.focus();
    requestAnimationFrame(() => dialog.classList.add('is-in'));
}

export function initModals() {
    document.querySelectorAll('dialog.pf-modal').forEach(function (dialog) {
        const content = dialog.querySelector('.pf-modal__content');
        const loader = content.innerHTML;
        let pressedOnBackdrop = false;

        // Escape: slide out like the close button instead of vanishing
        dialog.addEventListener('cancel', function (e) {
            e.preventDefault();
            closeModal(dialog);
        });

        // The dialog itself is the dimmed backdrop, the box is .pf-modal__dialog. A press that started inside the box
        // and ended outside (selecting text) doesn't close it
        dialog.addEventListener('mousedown', function (e) {
            pressedOnBackdrop = e.target === dialog;
        });
        dialog.addEventListener('click', function (e) {
            if (pressedOnBackdrop && e.target === dialog) closeModal(dialog);
        });

        dialog.addEventListener('close', function () {
            dialog.cancelClosing?.();
            dialog.cancelClosing = null;
            dialog.classList.remove('is-in', 'is-closing');
            lockScroll(false);
            dialog.dispatchEvent(new CustomEvent('modal:hidden', {bubbles: true}));
            content.innerHTML = loader;
        });
    });

    on('click', '[data-modal-close]', (e, button) => closeModal(button.closest('dialog')));
}
